import { z } from "zod";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "../pagination";
import { type NocRef, versionField } from "./common";
import type { MeetDisciplineSummary, MeetEventSummary, MeetSportSummary } from "./meets";

/**
 * Records — the record book OVR shows beside an event (migration 0053,
 * `event_records`), and the record marks results set at these games.
 *
 *   GET    /records?games_id=…           Paginated<EventRecordItem>   the book
 *   POST   /records                       EventRecordItem              add one
 *   PATCH  /records/:id                   EventRecordItem              edit (carries `version`)
 *   DELETE /records/:id                   null
 *   GET    /records/marks?games_id=…      Paginated<RecordMarkItem>    results that carry a mark
 *
 * The old OVR kept records inside each event as a JSON array, with holders as
 * free text and no link between a result's "record break" flag and the book.
 * Here a record is a row: of an EVENT of the games, or — for an event these
 * games do not run (an old distance, a discontinued class) — of a sport and a
 * free-text `event_label`, never both (CHECK). The holder stays free text on
 * purpose: most holders set their record at other competitions and have no
 * card in this system.
 *
 * Breaking a record is not automated (as before): the meet screen marks a
 * result GR / NR (`meet_competitors.records`), `/records/marks` lists those
 * marks, and an operator updates the book.
 *
 * Reads: every competition reader. Writes: `COMPETITION_SETUP_ROLES` — the
 * book is reference data, like events.
 */

// ─── Vocabulary ──────────────────────────────────────────────────────────────

/**
 * Record types, in rank order (a world record first). The codes are the marks
 * a result carries (`RESULT_RECORD_CODE_PATTERN`), so a GR on a result and a
 * GR in the book are the same word. GR = a record of these games (SEA Games,
 * or ASEAN Para Games on the para side).
 */
export const EVENT_RECORD_TYPES = ["WR", "OR", "PR", "AR", "GR", "NR"] as const;
export type EventRecordType = (typeof EVENT_RECORD_TYPES)[number];

export const EVENT_RECORD_MARK_MAX = 40;
export const EVENT_RECORD_HOLDER_MAX = 200;
export const EVENT_RECORD_NOC_MAX = 60;
export const EVENT_RECORD_LABEL_MAX = 120;
export const EVENT_RECORD_TEXT_MAX = 160;
export const EVENT_RECORD_NOTE_MAX = 500;
export const EVENT_RECORDS_PAGE_SIZE_DEFAULT = 50;

// ─── Fields ──────────────────────────────────────────────────────────────────

const idField = z.string().uuid().toLowerCase();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
/** Trimmed FIRST (bug 5); empty = not given. */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.transform((s) => (s === "" ? null : s))
		.nullable()
		.optional();
const requiredText = (max: number) => z.string().trim().min(1).max(max);

// ─── Queries ─────────────────────────────────────────────────────────────────

/**
 * `GET /records` — the book of one games, in programme order (sport,
 * discipline, event — external events after the games' own, by label), then
 * type rank, then the newest date. Offset pages: a games' book is hundreds of
 * rows. `search` matches the holder, their NOC, the mark, the event's name or
 * label, the competition and the venue.
 */
export const listEventRecordsQuery = z.object({
	games_id: idField,
	sport_id: idField.optional(),
	discipline_id: idField.optional(),
	event_id: idField.optional(),
	record_type: z.enum(EVENT_RECORD_TYPES).optional(),
	/** Omitted = active and inactive. */
	active: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
	search: searchParam.optional(),
	page: paginationQuery.shape.page,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(EVENT_RECORDS_PAGE_SIZE_DEFAULT),
});
export type ListEventRecordsQuery = z.input<typeof listEventRecordsQuery>;

/**
 * `GET /records/marks` — results of the games that carry a record mark,
 * newest result first. Offset pages; the set is small (a partial index holds
 * only marked results).
 */
export const listRecordMarksQuery = z.object({
	games_id: idField,
	sport_id: idField.optional(),
	/** Only this organisation's results (the Contingent Dashboard's Records tab). */
	responsible_organization_id: idField.optional(),
	record_type: z.string().trim().regex(/^[A-Z]{2,4}$/).optional(),
	page: paginationQuery.shape.page,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(EVENT_RECORDS_PAGE_SIZE_DEFAULT),
});
export type ListRecordMarksQuery = z.input<typeof listRecordMarksQuery>;

// ─── Bodies ──────────────────────────────────────────────────────────────────

const recordFields = {
	record_type: z.enum(EVENT_RECORD_TYPES),
	mark: requiredText(EVENT_RECORD_MARK_MAX),
	holder_name: requiredText(EVENT_RECORD_HOLDER_MAX),
	holder_noc: optionalText(EVENT_RECORD_NOC_MAX),
	set_on: isoDate.nullable().optional(),
	venue: optionalText(EVENT_RECORD_TEXT_MAX),
	competition: optionalText(EVENT_RECORD_TEXT_MAX),
	note: optionalText(EVENT_RECORD_NOTE_MAX),
	is_active: z.boolean().optional(),
};

/**
 * `POST /records`. Either `event_id` (an event of the games: its sport and
 * discipline are taken from it, never from the body), or `sport_id` +
 * `event_label` (+ `discipline_id`, which must be of that sport) for an event
 * these games do not run.
 */
export const createEventRecordBody = z
	.object({
		event_id: idField.optional(),
		sport_id: idField.optional(),
		discipline_id: idField.nullable().optional(),
		event_label: optionalText(EVENT_RECORD_LABEL_MAX),
		...recordFields,
	})
	.superRefine((b, ctx) => {
		if (b.event_id) {
			if (b.event_label) ctx.addIssue({ code: "custom", path: ["event_label"], message: "Give an event or a label, not both" });
			return;
		}
		if (!b.sport_id) ctx.addIssue({ code: "custom", path: ["sport_id"], message: "Pick the sport" });
		if (!b.event_label) ctx.addIssue({ code: "custom", path: ["event_label"], message: "Name the event" });
	});
export type CreateEventRecordBody = z.input<typeof createEventRecordBody>;

/** `PATCH /records/:id` — the record's fields; where it is filed (event / label) never changes: delete and add. */
export const updateEventRecordBody = z.object({
	version: versionField,
	record_type: recordFields.record_type.optional(),
	mark: recordFields.mark.optional(),
	holder_name: recordFields.holder_name.optional(),
	holder_noc: recordFields.holder_noc,
	set_on: recordFields.set_on,
	venue: recordFields.venue,
	competition: recordFields.competition,
	note: recordFields.note,
	is_active: recordFields.is_active,
	/** The book's label for an external record; refused (422) on a record of an event. */
	event_label: z.string().trim().min(1).max(EVENT_RECORD_LABEL_MAX).optional(),
});
export type UpdateEventRecordBody = z.input<typeof updateEventRecordBody>;

// ─── Responses ───────────────────────────────────────────────────────────────

export interface EventRecordItem {
	event_record_id: string;
	sport: MeetSportSummary;
	discipline: Pick<MeetDisciplineSummary, "discipline_id" | "code" | "name"> | null;
	/** The games' event; null for an external record, which has `event_label`. */
	event: MeetEventSummary | null;
	event_label: string | null;
	record_type: EventRecordType;
	mark: string;
	holder_name: string;
	holder_noc: string | null;
	set_on: string | null;
	venue: string | null;
	competition: string | null;
	note: string | null;
	is_active: boolean;
	version: number;
	updated_at: string;
}

/** One result that carries a record mark. */
export interface RecordMarkItem {
	meet_competitor_id: string;
	meet_id: string;
	meet_name: string;
	/** When the meet is scheduled (ISO, UTC); null when it has no time. */
	scheduled_at: string | null;
	sport: MeetSportSummary;
	event: MeetEventSummary;
	/** The printed name of the entry: the athlete, or the team. */
	competitor_name: string;
	noc: NocRef | null;
	records: string[];
	result_display: string | null;
	rank: number | null;
	result_updated_at: string | null;
}

/** Display rank of a record type (0 = a world record); unknown codes last. */
export const eventRecordRank = (type: string): number => {
	const i = (EVENT_RECORD_TYPES as readonly string[]).indexOf(type);
	return i === -1 ? EVENT_RECORD_TYPES.length : i;
};
